'use client'

/**
 * THE ball. One drawing, four surfaces (B20 Task 2 item 1): the capsule's score
 * slots and its rolling-ball scene, every tile and count bar on the grid, the
 * past-rounds rows and the round sheet's outcome row. Before B20 the capsule
 * drew a real golf ball and the tiles drew an abstract disc/ring pair, so the
 * same three facts were told in two visual languages on one screen.
 *
 * The two radial gradients are declared ONCE per tree by `<BirdieBallDefs>` and
 * referenced by FIXED ids. That is deliberate and is the change from B16's
 * `useId` pair: a document can hold several trees that draw balls (the desktop
 * overlay, the mobile strip, the portalled round sheet), and unique-per-tree ids
 * meant every tree had to carry its own defs or silently bind to another tree's.
 * With one shared id the defs are IDEMPOTENT — a second, identical definition
 * changes nothing, because a duplicate SVG id binds every reference to the first
 * element and the first element is the same drawing. So each tree may render the
 * defs without knowing whether another already has, and none of them can drift.
 *
 * `<BirdieBallDefs>` must be somewhere in the document for a ball to paint. SVG
 * paint servers resolve at paint time, so it may come before or after the balls
 * that use it.
 */

/** Sphere gradient id — shared, fixed, and therefore safe to re-declare. */
export const BIRDIE_BALL_SPHERE_ID = 'birdie-ball-sphere'
/** Specular-highlight gradient id. Same contract as the sphere. */
export const BIRDIE_BALL_HIGHLIGHT_ID = 'birdie-ball-highlight'

/**
 * The ball's white when its gradient does not paint (owner 10-29, Safari: "i
 * just see the dimples on the golf ball but not the actual white color"). It is
 * drawn as a plain disc UNDER the sphere and is also the fill's own fallback
 * colour, so a ball is white whether or not a browser resolves the shared
 * gradient for it; where the gradient paints it covers this disc completely
 * (its outer stop pads to the edge), so the drawing is unchanged there.
 */
export const BIRDIE_BALL_BASE = '#e9eef1'
/** `fill` for a sphere: the shared gradient, falling back to the plain white. */
export const BIRDIE_BALL_SPHERE_FILL = `url(#${BIRDIE_BALL_SPHERE_ID}) ${BIRDIE_BALL_BASE}`

/**
 * The dimple field. Thirteen fixed positions rather than a generated grid: they
 * were placed by eye to read as a ball at 28 px and still not turn to mush at
 * 14 px, and a loop over them would be a loop whose output nobody can check.
 */
const DIMPLES: readonly (readonly [number, number, number])[] = [
  [8.2, 8.6, 0.75],
  [11.3, 7, 0.75],
  [14.6, 7.8, 0.75],
  [16.8, 10.6, 0.75],
  [15.3, 13.7, 0.75],
  [12.4, 11.3, 0.75],
  [9.4, 12, 0.75],
  [7.1, 14.6, 0.75],
  [10.3, 16.2, 0.75],
  [13.6, 16.6, 0.75],
  [16.4, 16.3, 0.7],
  [12.9, 19.2, 0.7],
  [9.1, 19, 0.7]
]

/**
 * One ball. `className` is the caller's styling hook (size, filters, the
 * capsule's drop and squash animations); the drawing itself carries no size, so
 * every surface decides it in CSS and the ball is the same at 11 px and 28 px.
 */
export function GolfBall({ className = 'birdie-ball' }: Readonly<{ className?: string }>) {
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 24 24">
      <circle cx="12" cy="12" fill={BIRDIE_BALL_BASE} r="10" />
      <circle cx="12" cy="12" fill={BIRDIE_BALL_SPHERE_FILL} r="10" />
      <g className="birdie-ball__dimples">
        {DIMPLES.map(([cx, cy, r]) => (
          <circle cx={cx} cy={cy} key={`${cx}-${cy}`} r={r} />
        ))}
      </g>
      <ellipse
        cx="8.6"
        cy="7.2"
        fill={`url(#${BIRDIE_BALL_HIGHLIGHT_ID})`}
        rx="3"
        ry="1.8"
        transform="rotate(-28 8.6 7.2)"
      />
    </svg>
  )
}

/**
 * The two gradients every ball shares. Render once per tree that draws balls —
 * rendering it twice is a no-op, rendering it never leaves the balls unpainted.
 */
export function BirdieBallDefs() {
  return (
    <svg aria-hidden="true" className="birdie-ball-defs" height="0" width="0">
      <defs>
        {/*
          B40 — THE BALL READS AS A SPHERE, NOT A DISC.
          Same one gradient and the same element count as before: what changed is
          the stop ramp. A lit sphere is four bands, not three — a bright
          key-lit cap, a mid tone, a DARK TERMINATOR just inside the silhouette,
          and then a rim light back up on the far edge where the ground bounces
          into it. The old ramp ran monotonically from white to a single mid grey,
          which is what a flat disc with a vignette looks like; at the 13-16 px the
          tiles draw, the rim is the whole difference between a ball and a dot.

          The offsets are radii from the key light at 36 % / 30 %, so `1` is the
          bottom-right limb — the edge furthest from the light, which is exactly
          where a bounce rim belongs.
        */}
        <radialGradient cx="36%" cy="30%" id={BIRDIE_BALL_SPHERE_ID} r="72%">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="0.38" stopColor="#f2f6f8" />
          <stop offset="0.72" stopColor="#c6d1d6" />
          <stop offset="0.9" stopColor="#8d9ba2" />
          <stop offset="1" stopColor="#b8c5ca" />
        </radialGradient>
        {/* Tighter and brighter than B20's: a specular is a small hot spot. */}
        <radialGradient cx="50%" cy="50%" id={BIRDIE_BALL_HIGHLIGHT_ID} r="50%">
          <stop offset="0" stopColor="#fff" stopOpacity="1" />
          <stop offset="0.55" stopColor="#fff" stopOpacity="0.55" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
      </defs>
    </svg>
  )
}
